import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef, useMemo } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { logger } from '@/utils/logger';
import { monitoring } from '@/lib/monitoring';
import { clearRecoveryLock, isRecoveryLocked } from '@/lib/recoveryLock';
import { clearScopedDrafts } from '@/lib/scopedDraftStorage';

interface CachedRolePermission {
  role: string;
  feature_key: string;
  is_enabled: boolean;
}

export type UserRole =
  | 'master'
  | 'vendedor_junior'
  | 'vendedor_senior'
  | 'financeiro_junior'
  | 'financeiro_senior'
  | 'gestor_comercial'
  | 'comprador'
  | 'estoquista';

interface Company {
  id: string;
  name: string;
  razao_social?: string;
  cnpj?: string;
  domain?: string;
  endereco?: string;
  numero?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
  cep?: string;
  telefone?: string;
  email_corporativo?: string;
  chave_pix?: string;
  subscription_plan: string;
  active: boolean;
  stripe_customer_id?: string;
  stripe_subscription_id?: string;
  subscription_expires_at?: string;
  max_users?: number;
  logo_url?: string;
  // Campos de status do tenant (KYB)
  tenant_status?: 'pending_approval' | 'active' | 'suspended' | 'banned';
  suspended_reason?: string | null;
}

interface Profile {
  id: string;
  user_id: string;
  full_name: string;
  email: string;
  avatar_url?: string;
  company_id?: string;
  company?: Company;
  must_change_password?: boolean;
  phone?: string;
  document_number?: string;
  birth_date?: string;
  isComplete?: boolean; // ✅ Flag para perfil completo
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  userRole: UserRole | null;
  userRoles: UserRole[];
  profile: Profile | null;
  company: Company | null;
  loading: boolean;
  isPermissionsLoading: boolean;
  needsCompany: boolean;
  subscriptionExpired: boolean;
  signIn: (email: string, password: string) => Promise<{ error: any; role?: UserRole | null }>;
  signUp: (email: string, password: string, fullName: string, companyName: string, phone?: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  hasPermission: (requiredRole: UserRole) => boolean;
  canAccess: (feature: string, context?: string) => boolean;
  updateUserCompany: (companyId: string) => Promise<void>;
  createCompany: (companyName: string) => Promise<{ error: any; company?: Company }>;
  fetchUserProfile: () => Promise<string | null>;
  refreshProfile: () => Promise<void>;
  refreshRolePermissions: () => Promise<void>;
  authError: string | null;
  debugInfo: string;
  // Multiloja — Sprint 2
  organizationId: string | null;
  accessibleCompanyIds: string[];
  isOrgMember: boolean;
  activeCompanyId: string | null;
  switchCompanyContext: (targetCompanyId: string | null) => Promise<void>;
  refreshOrgClaims: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    // Graceful fallback: retorna estado seguro com loading=true
    // em vez de throw, evitando crash do ErrorBoundary em edge-cases
    return {
      user: null, session: null, userRole: null, userRoles: [],
      profile: null, company: null, loading: true,
      isPermissionsLoading: true,
      needsCompany: false, subscriptionExpired: false,
      signIn: async () => ({ error: 'AuthProvider not initialized' }),
      signUp: async () => ({ error: 'AuthProvider not initialized' }),
      signOut: async () => {},
      resetPassword: async () => ({ error: 'AuthProvider not initialized' }),
      hasPermission: () => false,
      canAccess: () => false,
      updateUserCompany: async () => {},
      createCompany: async () => ({ error: 'AuthProvider not initialized' }),
      fetchUserProfile: async () => null,
      refreshProfile: async () => {},
      refreshRolePermissions: async () => {},
      refreshOrgClaims: async () => {},
      authError: null,
      debugInfo: 'AuthProvider not mounted',
      organizationId: null,
      accessibleCompanyIds: [],
      isOrgMember: false,
      activeCompanyId: null,
      switchCompanyContext: async () => {},
    } as AuthContextType;
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

// ✅ Importado de constants/permissions.ts
import { ROLE_HIERARCHY } from '@/constants/permissions';

const getJwtClaims = (session: Session | null) => {
  if (!session) return null;
  if (!session.access_token) return null;
  try {
    const payload = JSON.parse(atob(session.access_token.split('.')[1]));
    return {
      company_id: payload.company_id as string | null,
      user_roles: (payload.user_roles as string[]) ?? [],
      feature_keys: (payload.feature_keys as string[]) ?? [],
      organization_id: (payload.organization_id as string) ?? null,
      accessible_company_ids: (payload.accessible_company_ids as string[]) ?? [],
      is_org_member: (payload.is_org_member as boolean) ?? false,
    };
  } catch (err: unknown) {
    monitoring.captureWarning('jwt_decode_failed', {
      error: err instanceof Error ? err.message : String(err),
    });
    return null;
  }
};

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [userRole, setUserRole] = useState<UserRole | null>(null);
  const [userRoles, setUserRoles] = useState<UserRole[]>([]);
  const [rolePermissionsCache, setRolePermissionsCache] = useState<CachedRolePermission[]>([]);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPermissionsLoading, setIsPermissionsLoading] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [needsCompany, setNeedsCompany] = useState(false);
  const [subscriptionExpired, setSubscriptionExpired] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>('');
  // Multiloja — Sprint 2
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  const [accessibleCompanyIds, setAccessibleCompanyIds] = useState<string[]>([]);
  const [isOrgMember, setIsOrgMember] = useState(false);
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const retryCountRef = useRef<{[key: string]: number}>({});
  const isInitializedRef = useRef(false);
  const isInitializingRef = useRef(false);
  // Garante que isPermissionsLoading só transite true→false UMA ÚNICA VEZ por sessão.
  // Após a primeira carga completa, nunca volta a true (imune a refreshes e TOKEN_REFRESHED).
  const permissionsLoadedRef = useRef(false);
  const isDevelopment = import.meta.env.DEV;
  
  // Refs para prevenção de múltiplos listeners
  const loadingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // A-ASS.7: espelho do estado para o timeout de 30s ler o valor atual (a closure do mount é sempre true)
  const loadingRef = useRef(loading);
  loadingRef.current = loading;
  const profileTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // ✅ NOVO: Debounce para inicialização
  const lastInitTimeRef = useRef<number>(0);
  const MIN_INIT_INTERVAL = 5000; // 5 segundos

  // Função auxiliar para logout (usado em vários lugares)
  const performSignOut = useCallback(async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  }, []);

  // Helper para classificar e tratar erros - NUNCA força logout por erros temporários
  const handleAuthError = useCallback(async (
    error: any,
    context: string,
    retryCount: number,
    maxRetries: number = 2
  ): Promise<{ shouldRetry: boolean; shouldLogout: boolean }> => {
    logger.warn(`⚠️ ${context}: Erro detectado:`, error);
    
    // Erros de rede temporários - NUNCA forçar logout
    if (error.message?.includes('Failed to fetch') || 
        error.message?.includes('NetworkError') ||
        error.message?.includes('timeout')) {
      
      if (retryCount < maxRetries) {
        const delay = Math.pow(2, retryCount) * 1000;
        logger.log(`🔄 ${context}: Erro de rede, tentando novamente em ${delay}ms (${retryCount + 1}/${maxRetries})`);
        return { shouldRetry: true, shouldLogout: false };
      }
      
      logger.warn(`⚠️ ${context}: Máximo de tentativas atingido, mas mantendo usuário logado`);
      toast({
        title: "Problema de conexão",
        description: "Alguns dados podem estar desatualizados. Verifique sua internet.",
        variant: "default"
      });
      return { shouldRetry: false, shouldLogout: false };
    }
    
    // Rate limiting - apenas aviso
    if (error.code === '429' || error.message?.includes('rate limit')) {
      logger.warn(`⚠️ ${context}: Rate limit atingido`);
      toast({
        title: "Muitas requisições",
        description: "Aguarde alguns segundos.",
        variant: "default"
      });
      return { shouldRetry: false, shouldLogout: false };
    }
    
    // Outros erros - apenas log, não desconectar
    logger.warn(`⚠️ ${context}: Erro não crítico:`, error);
    return { shouldRetry: false, shouldLogout: false };
  }, [toast]);

  const fetchUserRole = useCallback(async (userId: string, retryCount = 0): Promise<UserRole[]> => {

    try {
      const { data, error } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', userId);

      if (error) {
        const { shouldRetry } = await handleAuthError(
          error, 
          'FETCH_ROLE', 
          retryCount
        );
        
        if (shouldRetry && retryCount < 2) {
          const delay = Math.pow(2, retryCount) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchUserRole(userId, retryCount + 1);
        }
        
        logger.warn('⚠️ FETCH_ROLE: Retornando role padrão após erro');
        return ['vendedor_junior' as UserRole];
      }

      if (!data || data.length === 0) {
        return ['vendedor_junior' as UserRole];
      }

      // Sort by hierarchy descending so highest role is first
      const roles = (data.map(d => d.role as UserRole))
        .sort((a, b) => (ROLE_HIERARCHY[b] ?? 0) - (ROLE_HIERARCHY[a] ?? 0));

      return roles;
    } catch (error) {
      if (retryCount < 2) {
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchUserRole(userId, retryCount + 1);
      }
      logger.warn('⚠️ FETCH_ROLE: Erro ao buscar role:', error);
      return ['vendedor_junior' as UserRole];
    }
  }, [handleAuthError]);

  /** Retorna company_id resolvido do perfil (para init — evita ler companyRef antes do re-render). */
  const fetchUserProfile = useCallback(async (userId?: string, retryCount = 0): Promise<string | null> => {
    const targetUserId = userId || user?.id;
    if (!targetUserId) {
      logger.warn('⚠️ FETCH_PROFILE: userId não fornecido');
      return null;
    }

    logger.log(`🔍 FETCH_PROFILE: Iniciando busca do profile para userId: ${targetUserId} (tentativa ${retryCount + 1})`);

    try {
      // Primeira tentativa: consulta com JOIN
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select(`
          *,
          company:companies(*)
        `)
        .eq('user_id', targetUserId)
        .single();

      logger.log('🔍 FETCH_PROFILE: Resultado da consulta com JOIN:', {
        profileData,
        profileError,
        hasCompany: !!profileData?.company,
        companyId: profileData?.company_id
      });

      if (profileError) {
        logger.warn('⚠️ FETCH_PROFILE: Erro na consulta com JOIN:', profileError);
        
        // Detectar usuário sem perfil e forçar logout
        if (profileError.code === 'PGRST116') {
          logger.error('❌ FETCH_PROFILE: Perfil não encontrado (PGRST116) - usuário deletado ou sem perfil');
          
          await supabase.auth.signOut();
          
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-')) {
              localStorage.removeItem(key);
            }
          });
          
          setAuthError('Sua conta não foi encontrada no sistema. Entre em contato com o administrador.');
          setUser(null);
          setSession(null);
          setProfile(null);
          setCompany(null);
          setNeedsCompany(false);
          return null;
        }
        
        const { shouldRetry } = await handleAuthError(
          profileError,
          'FETCH_PROFILE',
          retryCount
        );
        
        if (shouldRetry && retryCount < 2) {
          const delay = Math.pow(2, retryCount) * 1000;
          await new Promise(resolve => setTimeout(resolve, delay));
          return fetchUserProfile(targetUserId, retryCount + 1);
        }
        
        // Fallback: buscar profile e company separadamente
        logger.log('🔄 FETCH_PROFILE: Tentando consulta de fallback separada...');
        return fetchUserProfileFallback(targetUserId);
      }

      logger.log('✅ FETCH_PROFILE: Profile encontrado:', {
        id: profileData.id,
        user_id: profileData.user_id,
        company_id: profileData.company_id,
        hasCompanyData: !!profileData.company,
        companyName: profileData.company?.name
      });

      // §I.146: gate de status — suspenso/inativado não entra
      const st = profileData.status;
      if (st && st !== 'active') {
        await supabase.auth.signOut();
        toast({
          title: "Acesso bloqueado",
          description: "Conta suspensa ou inativada. Contate o administrador.",
          variant: "destructive",
        });
        return null;
      }

      // Calcular isComplete
      const isComplete = !!(profileData.document_number && profileData.birth_date && profileData.phone && profileData.full_name);
      setProfile({ ...profileData, isComplete });
      
      // Lógica melhorada para needsCompany
      if (profileData.company_id && profileData.company) {
        logger.log('✅ FETCH_PROFILE: Usuário tem empresa válida:', profileData.company.name);
        setCompany(profileData.company);
        setNeedsCompany(false);
        
        // Check subscription status
        const isExpired = profileData.company.subscription_expires_at && 
          new Date(profileData.company.subscription_expires_at) < new Date();
        setSubscriptionExpired(isExpired || false);
        
        logger.log('📊 FETCH_PROFILE: Status da assinatura:', {
          expires_at: profileData.company.subscription_expires_at,
          isExpired: isExpired || false
        });
        return profileData.company_id;
      } else if (profileData.company_id && !profileData.company) {
        logger.warn('⚠️ FETCH_PROFILE: Profile tem company_id mas dados da empresa não vieram. Tentando buscar separadamente...');
        await fetchCompanyData(profileData.company_id);
        setNeedsCompany(false);
        return profileData.company_id;
      } else {
        logger.log('❌ FETCH_PROFILE: Usuário não tem empresa associada');
        setCompany(null);
        setNeedsCompany(true);
        setSubscriptionExpired(false);
        return null;
      }
    } catch (error) {
      logger.warn('⚠️ FETCH_PROFILE: Erro inesperado:', error);
      
      if (retryCount < 2) {
        logger.log(`🔄 FETCH_PROFILE: Tentando novamente (${retryCount + 1}/2)...`);
        const delay = Math.pow(2, retryCount) * 1000;
        await new Promise(resolve => setTimeout(resolve, delay));
        return fetchUserProfile(targetUserId, retryCount + 1);
      }
      
      // Após tentativas, falhar graciosamente mas manter usuário logado
      logger.warn('⚠️ FETCH_PROFILE: Não foi possível carregar perfil completo');
      setNeedsCompany(true);
      return null;
    }
    // BUG-08 FIX: setLoading(false) removido daqui — controlado exclusivamente por initializeUserData
  }, [user?.id]);

  // Função para recarregar o perfil do usuário (útil após atualizações)
  const refreshProfile = useCallback(async () => {
    if (user?.id) {
      await fetchUserProfile(user.id);
    }
  }, [user?.id, fetchUserProfile]);

  // Função auxiliar para buscar dados da empresa
  const fetchCompanyData = useCallback(async (companyId: string) => {
    logger.log('🏢 COMPANY: Buscando dados da empresa:', companyId);
    
    try {
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .select('*')
        .eq('id', companyId)
        .single();

      if (companyError) {
        logger.error('❌ COMPANY: Erro ao buscar empresa:', companyError);
        setCompany(null);
        setNeedsCompany(true); // Se não conseguir buscar a empresa, precisa selecionar
        return;
      }

      logger.log('✅ COMPANY: Empresa encontrada:', {
        id: companyData.id,
        name: companyData.name,
        active: companyData.active
      });

      setCompany(companyData);
      setNeedsCompany(false);
      
      // Check subscription status
      const isExpired = companyData.subscription_expires_at && 
        new Date(companyData.subscription_expires_at) < new Date();
      setSubscriptionExpired(isExpired || false);
      
      logger.log('📊 COMPANY: Status da assinatura:', {
        expires_at: companyData.subscription_expires_at,
        isExpired: isExpired || false
      });
    } catch (error) {
      logger.error('❌ COMPANY: Erro inesperado ao buscar empresa:', error);
      setCompany(null);
      setNeedsCompany(true);
    }
  }, []);

  // Função de fallback para buscar profile e company separadamente
  const fetchUserProfileFallback = useCallback(async (userId: string): Promise<string | null> => {
    logger.log('🔄 FALLBACK: Iniciando consulta de fallback para userId:', userId);
    
    try {
      // Buscar apenas o profile
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', userId)
        .single();

      if (profileError) {
        logger.error('❌ FALLBACK: Erro ao buscar profile:', profileError);
        
        // ✅ NOVO: Se perfil não existe, forçar logout
        if (profileError.code === 'PGRST116') {
          logger.error('❌ FALLBACK: Usuário sem perfil (PGRST116) - forçando logout');
          await supabase.auth.signOut();
          
          // Limpar localStorage
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-')) {
              localStorage.removeItem(key);
            }
          });
          
          setAuthError('Conta não encontrada no sistema. Contate o administrador.');
          setUser(null);
          setSession(null);
          setProfile(null);
          setCompany(null);
          setLoading(false);
        }
        return null;
      }

      logger.log('✅ FALLBACK: Profile encontrado:', {
        id: profileData.id,
        user_id: profileData.user_id,
        company_id: profileData.company_id
      });

      // §I.146: gate de status — suspenso/inativado não entra
      const st = profileData.status;
      if (st && st !== 'active') {
        await supabase.auth.signOut();
        toast({
          title: "Acesso bloqueado",
          description: "Conta suspensa ou inativada. Contate o administrador.",
          variant: "destructive",
        });
        return null;
      }

      setProfile(profileData);

      if (profileData.company_id) {
        logger.log('🔍 FALLBACK: Buscando dados da empresa:', profileData.company_id);
        await fetchCompanyData(profileData.company_id);
        setNeedsCompany(false);
        return profileData.company_id;
      } else {
        logger.log('❌ FALLBACK: Usuário não tem empresa associada');
        setCompany(null);
        setNeedsCompany(true);
        setSubscriptionExpired(false);
        return null;
      }
    } catch (error) {
      logger.error('❌ FALLBACK: Erro inesperado:', error);
      return null;
    }
  }, [fetchCompanyData]);

  const updateUserCompany = useCallback(async (companyId: string) => {
    if (!user) return;

    try {
      const { error } = await supabase
        .from('profiles')
        .update({ company_id: companyId })
        .eq('user_id', user.id);

      if (error) {
        toast({
          title: "Erro",
          description: "Não foi possível atualizar a empresa",
          variant: "destructive",
        });
        return;
      }

      // Refresh profile data
      await fetchUserProfile(user.id);
      
      toast({
        title: "Sucesso",
        description: "Empresa atualizada com sucesso!",
      });
    } catch (error) {
      logger.error('Error updating user company:', error);
    }
  }, [user, fetchUserProfile, toast]);

  const createCompany = useCallback(async (companyName: string) => {
    if (!user) return { error: new Error('User not authenticated') };

    try {
      // Create company
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .insert([{ name: companyName }])
        .select()
        .single();

      if (companyError) {
        return { error: companyError };
      }

      // Update user profile with company_id
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ company_id: companyData.id })
        .eq('user_id', user.id);

      if (updateError) {
        return { error: updateError };
      }

      // Make user master of their own company via upsert (safe for multi-role)
      const { error: roleError } = await supabase
        .from('user_roles')
        .upsert(
          { user_id: user.id, role: 'master' },
          { onConflict: 'user_id,role' }
        );

      if (roleError) {
        logger.warn('Failed to upsert user role to master:', roleError);
      }

      // Refresh profile data
      await fetchUserProfile(user.id);
      setUserRoles(['master']);
      setUserRole('master');

      return { error: null, company: companyData };
    } catch (error) {
      logger.error('Error creating company:', error);
      return { error };
    }
  }, [user, fetchUserProfile]);

  // Ref to avoid stale closure on userRoles without causing useCallback re-creation
  const userRolesRef = useRef(userRoles);
  userRolesRef.current = userRoles;

  // ✅ Ref para company — evita stale closure em initializeUserData
  const companyRef = useRef(company);
  companyRef.current = company;

  // ✅ Ref para session — permite leitura do JWT sem adicionar session como dep de useCallback
  const sessionRef = useRef(session);
  sessionRef.current = session;

  // Fetch role_permissions from DB and cache them in state (BUG-02/DRY-01 fix).
  // IMPORTANTE: NÃO seta isPermissionsLoading=true aqui — a flag é one-way (true→false)
  // e gerenciada exclusivamente por permissionsLoadedRef para evitar o bug de skeleton
  // ao navegar (TOKEN_REFRESHED, refreshes do painel de config, etc).
  const fetchRolePermissions = useCallback(async (companyId: string): Promise<void> => {
    try {
      const { data, error } = await supabase
        .from('role_permissions')
        .select('role, feature_key, is_enabled')
        .eq('company_id', companyId);

      if (error) {
        logger.warn('⚠️ ROLE_PERMS: Erro ao buscar permissões:', error);
        return;
      }

      if (data && data.length > 0) {
        setRolePermissionsCache(data as CachedRolePermission[]);
        logger.log(`✅ ROLE_PERMS: ${data.length} permissões carregadas do banco`);
      } else {
        // Auto-seed if empty
        try {
          await supabase.rpc('ensure_default_role_permissions', { p_company_id: companyId });
          const { data: seeded } = await supabase
            .from('role_permissions')
            .select('role, feature_key, is_enabled')
            .eq('company_id', companyId);
          if (seeded) {
            setRolePermissionsCache(seeded as CachedRolePermission[]);
            logger.log(`✅ ROLE_PERMS: ${seeded.length} permissões semeadas e carregadas`);
          }
        } catch (seedErr) {
          logger.warn('⚠️ ROLE_PERMS: Erro ao semear permissões:', seedErr);
        }
      }
    } catch (err) {
      logger.warn('⚠️ ROLE_PERMS: Erro inesperado:', err);
    } finally {
      // Transição one-way: só desliga o loading na PRIMEIRA carga bem-sucedida.
      // Chamadas subsequentes (TOKEN_REFRESHED, painel de config) não devem
      // recolocar a Sidebar em skeleton.
      if (!permissionsLoadedRef.current) {
        permissionsLoadedRef.current = true;
        setIsPermissionsLoading(false);
      }
    }
  }, []);

  // Initialize user data when authenticated — returns resolved roles array
  // sessionOverride: JWT da sessão atual (obrigatório após setSession — sessionRef ainda pode estar stale até o próximo render)
  const initializeUserData = useCallback(async (userId: string, sessionOverride?: Session | null): Promise<UserRole[]> => {
    const now = Date.now();
    
    if (now - lastInitTimeRef.current < MIN_INIT_INTERVAL && isInitializedRef.current) {
      logger.log('⏭️ INIT: Inicialização recente, pulando...');
      return userRolesRef.current;
    }
    
    if (isInitializingRef.current) {
      logger.log('⏭️ INIT: Inicialização já em andamento, pulando...');
      return userRolesRef.current;
    }

    lastInitTimeRef.current = now;
    isInitializingRef.current = true;
    setLoading(true);

    // BUG-09 FIX: Timeout de segurança controlado exclusivamente aqui (não na recursão de fetchUserProfile)
    profileTimeoutRef.current = setTimeout(() => {
      logger.error('⏰ INIT: Timeout de 30 segundos atingido na inicialização');
      setLoading(false);
      if (!permissionsLoadedRef.current) {
        permissionsLoadedRef.current = true;
        setIsPermissionsLoading(false);
      }
      monitoring.captureWarning('auth_init_timeout', {
        userId,
        elapsed: 30000,
      });
      setIsInitialLoading(false);
      isInitializingRef.current = false;
      setAuthError('Tempo limite ao carregar perfil. Tente novamente.');
    }, 30000);
    
    try {
      logger.log('🔄 INIT: Inicializando dados do usuário:', userId);
      setDebugInfo('Carregando dados do usuário...');
      setAuthError(null);
      
      const claims = getJwtClaims(sessionOverride !== undefined ? sessionOverride : sessionRef.current);

      if (claims?.company_id && claims.user_roles.length > 0) {
        // ✅ JWT fast-path: claims disponíveis — zero roundtrips para roles/permissões
        logger.log('⚡ INIT: JWT claims disponíveis — aplicando sem roundtrips', {
          roles: claims.user_roles,
          featureKeys: claims.feature_keys.length,
        });

        setUserRoles(claims.user_roles as UserRole[]);
        setUserRole((claims.user_roles[0] as UserRole) ?? 'vendedor_junior');

        // Reconstrói rolePermissionsCache a partir das feature_keys do JWT
        const syntheticCache = claims.feature_keys.map(fk => ({
          role: claims.user_roles[0] as string,
          feature_key: fk,
          is_enabled: true,
        }));
        setRolePermissionsCache(syntheticCache);

        // Multiloja — Sprint 2: aplicar claims de organização no fast-path
        setOrganizationId(claims.organization_id ?? null);
        setAccessibleCompanyIds(claims.accessible_company_ids ?? []);
        setIsOrgMember(claims.is_org_member ?? false);
        setActiveCompanyId(claims.company_id);

        if (!permissionsLoadedRef.current) {
          permissionsLoadedRef.current = true;
          setIsPermissionsLoading(false);
        }

        // Profile e company ainda precisam ser buscados (dados extras não estão no JWT)
        await fetchUserProfile(userId);

        const roles = claims.user_roles as UserRole[];
        // Sentry: identificar usuário sem PII (apenas IDs e role).
        monitoring.setUser(userId, claims.company_id, roles[0] ?? 'vendedor_junior');
        logger.log('✅ INIT: Fast-path concluído:', { userId, roles });
        setDebugInfo('Dados carregados com sucesso (JWT)');
        return roles;
      }

      // Fallback: JWT sem claims — mantém fluxo completo original
      logger.log('🔄 INIT: JWT sem claims — usando fluxo completo');
      const [roles, resolvedCompanyId] = await Promise.all([
        fetchUserRole(userId),
        fetchUserProfile(userId),
      ]);

      setUserRoles(roles);
      setUserRole(roles[0] || 'vendedor_junior');

      // company_id vindo do retorno do fetch — companyRef pode estar stale no mesmo tick do await
      if (resolvedCompanyId) {
        await fetchRolePermissions(resolvedCompanyId);
      }

      // Multiloja — Sprint 2: fallback para tokens sem claims de org
      setActiveCompanyId(resolvedCompanyId ?? null);

      // Sentry: identificar usuário (sem PII) também no fallback.
      if (resolvedCompanyId) {
        monitoring.setUser(userId, resolvedCompanyId, roles[0] ?? 'vendedor_junior');
      }

      logger.log('✅ INIT: Dados do usuário inicializados com sucesso:', { userId, roles });
      setDebugInfo('Dados carregados com sucesso');
      return roles;
      
    } catch (error) {
      logger.error('❌ INIT: Erro ao inicializar dados do usuário:', error);
      setAuthError(`Erro ao carregar dados do usuário: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      setDebugInfo(`Erro: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
      return ['vendedor_junior' as UserRole];
      
    } finally {
      logger.log('🏁 INIT: Finalizando inicialização');
      setLoading(false);
      // Safety net: se não havia empresa (fetchRolePermissions nunca foi chamado),
      // ou se houve erro antes do fetch, desbloqueia a UI de qualquer forma.
      if (!permissionsLoadedRef.current) {
        permissionsLoadedRef.current = true;
        setIsPermissionsLoading(false);
      }
      setIsInitialLoading(false);
      isInitializingRef.current = false;
      
      // Limpar timeout de profile se ainda ativo
      if (profileTimeoutRef.current) {
        clearTimeout(profileTimeoutRef.current);
        profileTimeoutRef.current = null;
      }
    }
  }, [fetchUserRole, fetchUserProfile, fetchRolePermissions]);

  // Ref estável para initializeUserData — permite que o useEffect principal
  // tenha deps mínimas sem ficar com closure obsoleto da função.
  // Resolve Bug 3: initializeUserData mudava de referência quando user?.id
  // transitava de null→ID, causando re-execução do useEffect e registro de
  // um segundo listener onAuthStateChange.
  const initializeUserDataRef = useRef(initializeUserData);
  initializeUserDataRef.current = initializeUserData;

  useEffect(() => {
    let isMounted = true;
    
    logger.log('🚀 AUTH: Iniciando AuthProvider...');
    setDebugInfo('Iniciando sistema de autenticação...');
    
    // Check for existing session FIRST (only once on mount)
    const AUTH_TIMEOUT = 15000;
    
    const initializeAuth = async () => {
      let authTimeoutId: ReturnType<typeof setTimeout>;
      try {
        const timeoutPromise = new Promise<never>((_, reject) => {
          authTimeoutId = setTimeout(() => reject(new Error('AUTH_TIMEOUT')), AUTH_TIMEOUT);
        });

        const { data: { session }, error } = await Promise.race([
          supabase.auth.getSession(),
          timeoutPromise
        ]);
        clearTimeout(authTimeoutId!);
        
        if (!isMounted) return;
        
        logger.log('📋 AUTH: Verificando sessão inicial:', session ? 'encontrada' : 'não encontrada');
        
        if (error) {
          logger.error('❌ AUTH: Erro ao verificar sessão:', error);
          // Limpar apenas chaves específicas do Supabase
          logger.log('🧹 AUTH: Limpando storage do Supabase por erro na sessão');
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith('sb-')) {
              localStorage.removeItem(key);
            }
          });
          setAuthError(`Erro de autenticação: ${error.message}`);
          setDebugInfo(`Erro ao verificar sessão: ${error.message}`);
          setLoading(false);
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);

        // AUTH-CADASTRO Ato 1: sessão de recuperação não carrega os dados de trabalho
        if (session?.user && isRecoveryLocked()) {
          logger.log('🔑 AUTH: Sessão de recuperação - sem inicializar dados');
          setLoading(false);
          return;
        }

        if (session?.user) {
          logger.log('✅ AUTH: Sessão válida encontrada para:', session.user.email);
          setDebugInfo('Sessão válida encontrada, carregando perfil...');
          isInitializedRef.current = true;
          await initializeUserDataRef.current(session.user.id, session);
        } else {
          // Tentar refresh antes de desistir (resilência iPadOS)
          logger.log('🔄 AUTH: Sessão nula, tentando refresh...');
          
          // Criar nova promise de timeout exclusiva para o refresh
          // A timeoutPromise original já foi consumida/limpa pelo primeiro Promise.race
          let refreshTimeoutId: ReturnType<typeof setTimeout>;
          const refreshTimeoutPromise = new Promise<never>((_, reject) => {
            refreshTimeoutId = setTimeout(() => reject(new Error('AUTH_TIMEOUT')), AUTH_TIMEOUT);
          });
          
          try {
            const { data: refreshData } = await Promise.race([
              supabase.auth.refreshSession(),
              refreshTimeoutPromise
            ]);
            clearTimeout(refreshTimeoutId!);

            if (refreshData?.session?.user) {
              logger.log('✅ AUTH: Sessão recuperada via refresh');
              setSession(refreshData.session);
              setUser(refreshData.session.user);
              setDebugInfo('Sessão recuperada automaticamente');
              isInitializedRef.current = true;
              // Usar ref para não capturar closure obsoleto de initializeUserData
              await initializeUserDataRef.current(refreshData.session.user.id, refreshData.session);
            } else {
              logger.log('⚠️ AUTH: Refresh falhou, sem sessão');
              clearRecoveryLock(); // AUTH-CADASTRO Ato 1: a trava só vale com sessão
              setDebugInfo('Nenhuma sessão válida encontrada');
              setLoading(false);
            }
          } catch (refreshError) {
            clearTimeout(refreshTimeoutId!);
            // Se o timeout estourou no refreshSession, ainda podemos tentar
            // inicializar com a sessão que já temos no state
            if (refreshError instanceof Error && refreshError.message === 'AUTH_TIMEOUT') {
              logger.warn('⏰ AUTH: Timeout no refresh, mas continuando sem bloquear...');
              // Não re-throw — apenas log e deixa o loading desligar
              setDebugInfo('Timeout no refresh de sessão');
              setLoading(false);
              return;
            }
            throw refreshError;
          }
        }
      } catch (e) {
        clearTimeout(authTimeoutId!);
        if (!isMounted) return;
        
        if (e instanceof Error && e.message === 'AUTH_TIMEOUT') {
          // Cancelar timeout global de 30s para evitar disparo tardio conflitante
          if (loadingTimeoutRef.current) {
            clearTimeout(loadingTimeoutRef.current);
            loadingTimeoutRef.current = null;
          }
          logger.error('⏰ AUTH: Timeout de 15s atingido na inicialização');
          setAuthError('Tempo limite ao conectar. Verifique sua internet e tente novamente.');
          setDebugInfo('Timeout na autenticação');
          setLoading(false);
          return;
        }
        
        logger.error('❌ AUTH: Erro inesperado ao verificar sessão:', e);
        // Limpar apenas chaves específicas do Supabase
        logger.log('🧹 AUTH: Limpando storage do Supabase por erro inesperado');
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith('sb-')) {
            localStorage.removeItem(key);
          }
        });
        setAuthError('Erro ao verificar sessão. Por favor, faça login novamente.');
        setLoading(false);
      }
    };
    
    // Initialize auth state
    initializeAuth();
    
    // Set up auth state listener for future changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (!isMounted) return;
        
        logger.log(`🔐 AUTH_EVENT: Evento de autenticação: ${event}`);
        
        // Skip INITIAL_SESSION event to avoid duplicate initialization
        if (event === 'INITIAL_SESSION') {
          logger.log('⏭️ AUTH_EVENT: Ignorando evento INITIAL_SESSION');
          return;
        }
        
        // TOKEN_REFRESHED: apenas atualiza sessão — NUNCA re-inicializa dados/permissões.
        // Re-executar initializeUserData aqui causaria o bug de skeleton durante a navegação.
        if (event === 'TOKEN_REFRESHED') {
          logger.log('⏭️ AUTH_EVENT: TOKEN_REFRESHED - apenas atualizando sessão');
          setSession(session);
          setUser(session?.user ?? null);
          return;
        }
        
        // Ignorar USER_UPDATED se já estamos inicializados
        if (event === 'USER_UPDATED' && isInitializedRef.current) {
          logger.log('⏭️ AUTH_EVENT: Ignorando USER_UPDATED - já inicializado');
          setSession(session);
          setUser(session?.user ?? null);
          return;
        }
        
        // ✅ PASSWORD_RECOVERY: setar sessão mas NÃO inicializar dados do usuário
        // Isso permite que a página /reset-password funcione sem interferência do ProtectedRoute
        if (event === 'PASSWORD_RECOVERY') {
          logger.log('🔑 AUTH_EVENT: PASSWORD_RECOVERY - apenas setando sessão, sem inicializar dados');
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
          return;
        }

        // AUTH-CADASTRO Ato 1: no PKCE a recuperação chega como SIGNED_IN; com a trava, não carregar dados
        if (session?.user && isRecoveryLocked()) {
          logger.log(`🔑 AUTH_EVENT: ${event} com trava de recuperação - sem inicializar dados`);
          setSession(session);
          setUser(session.user);
          setLoading(false);
          return;
        }

        setDebugInfo(`Evento: ${event}, Sessão: ${session ? 'válida' : 'inválida'}`);
        
        // Atualizações síncronas de estado
        setSession(session);
        setUser(session?.user ?? null);
        setAuthError(null);
        
        if (session?.user) {
          logger.log('👤 AUTH_EVENT: Usuário autenticado:', session.user.id);
          
          // ✅ NOVO: Verificar se já está inicializando ANTES do setTimeout
          if (isInitializingRef.current) {
            logger.log('⏭️ AUTH_EVENT: Inicialização já em andamento');
            return;
          }
          
          isInitializedRef.current = true;
          
          // Deferir chamadas assíncronas com setTimeout(0).
          // Usar ref para sempre chamar a versão mais recente de initializeUserData
          // sem adicionar a função como dep do useEffect (que causaria re-registro do listener).
          setTimeout(() => {
            if (isMounted) {
              void initializeUserDataRef.current(session.user.id, session).catch((err: unknown) => {
                monitoring.captureWarning('auth_init_unhandled_rejection', {
                  error: err instanceof Error ? err.message : String(err),
                });
              });
            }
          }, 0);
        } else {
          logger.log('❌ AUTH_EVENT: Sem usuário autenticado');
          clearRecoveryLock(); // AUTH-CADASTRO Ato 1: a trava só vale com sessão (cobre SIGNED_OUT e troca que falhou)
          setDebugInfo('Nenhum usuário autenticado');
          isInitializedRef.current = false;
          isInitializingRef.current = false;
          // Resetar flag de permissões para que o próximo login carregue do zero
          permissionsLoadedRef.current = false;
          setIsPermissionsLoading(true);
          setRolePermissionsCache([]);
          setUserRoles([]);
          setUserRole(null);
          setProfile(null);
          setCompany(null);
          setNeedsCompany(false);
          setSubscriptionExpired(false);
          setOrganizationId(null);
          setAccessibleCompanyIds([]);
          setIsOrgMember(false);
          setActiveCompanyId(null);
          setLoading(false);
          // Sentry: remover identidade do usuário após logout/expiração de sessão.
          monitoring.clearUser();
        }
      }
    );

    // Timeout APENAS para carregamento inicial (30 segundos)
    if (!isInitializedRef.current) {
      loadingTimeoutRef.current = setTimeout(() => {
        // A-ASS.7: valor atual de loading (a closure do mount é sempre true)
        if (loadingRef.current && !isInitializedRef.current) {
          logger.warn('⏰ AUTH: Timeout de autenticação inicial - 30s');
          setLoading(false);
          setIsInitialLoading(false);
          setAuthError('O carregamento está demorando muito. Verifique sua conexão.');
          setDebugInfo('Timeout de autenticação inicial (30s)');
          toast({
            title: "Carregamento lento",
            description: "Verifique sua conexão com a internet.",
            variant: "default"
          });
        }
      }, 30000); // 30 segundos, mas só no carregamento inicial
    }

    return () => {
      isMounted = false;
      subscription.unsubscribe();
      
      // Limpar timeout de loading
      if (loadingTimeoutRef.current) {
        clearTimeout(loadingTimeoutRef.current);
        loadingTimeoutRef.current = null;
      }
    };
  // Deps: apenas `toast` (para o timeout de 30s).
  // `initializeUserData` é acessado via initializeUserDataRef para evitar que
  // mudanças na função (ex: user?.id null→ID após login) causem re-registro
  // do listener onAuthStateChange e re-execução de initializeAuth.
  }, [toast]);

  // Listener de visibilitychange para recuperar sessão no iPadOS
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!isInitializedRef.current) return;

      try {
        const { data: { session: currentSession } } = await supabase.auth.getSession();
        if (!currentSession) {
          logger.log('🔄 AUTH: Tab resumed sem sessão, tentando refresh...');
          const { data: refreshData } = await supabase.auth.refreshSession();
          if (refreshData?.session) {
            setSession(refreshData.session);
            setUser(refreshData.session.user);
            logger.log('✅ AUTH: Sessão recuperada no resume da aba');
          }
        }
      } catch (e) {
        logger.error('❌ AUTH: Erro ao verificar sessão no resume:', e);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  const signIn = useCallback(async (email: string, password: string): Promise<{ error: any; role?: UserRole | null }> => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        setLoading(false);
        return { error };
      }

      // AUTH-CADASTRO Ato 1: entrar com e-mail e senha desliga a trava de recuperação
      clearRecoveryLock();

      if (data.session?.user) {
        setSession(data.session);
        setUser(data.session.user);
        setAuthError(null);
        isInitializedRef.current = true;
        
        // initializeUserData now returns the resolved roles — no second query needed
        const resolvedRoles = await initializeUserData(data.session.user.id, data.session);
        const resolvedRole = resolvedRoles[0] || 'vendedor_junior' as UserRole;
        
        toast({
          title: "Login realizado com sucesso",
          description: "Bem-vindo ao sistema!",
        });
        
        return { error: null, role: resolvedRole };
      }

      setLoading(false);
      return { error: null };
    } catch (error: any) {
      setLoading(false);
      return { error };
    }
  }, [toast, initializeUserData]);

  const signUp = useCallback(async (email: string, password: string, fullName: string, companyName: string, phone?: string) => {
    try {
      const redirectUrl = `${window.location.origin}/`;
      
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: redirectUrl,
          data: {
            full_name: fullName,
            company_name: companyName,
            phone: phone || null,
          }
        }
      });

      // AUTH-CADASTRO Ato 2: e-mail já cadastrado volta "ok" sem identidades (confirmação de e-mail ligada);
      // só a mensagem no formulário, sem toast e sem aviso de sucesso
      if (!error && Array.isArray(data?.user?.identities) && data.user.identities.length === 0) {
        return { error: { message: 'Este e-mail já está cadastrado. Use Entrar ou Esqueci minha senha.' } };
      }

      if (error) {
        if (error.message.includes('User already registered')) {
          toast({
            title: "Usuário já cadastrado",
            description: "Este email já está cadastrado no sistema",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Erro no cadastro",
            description: error.message,
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Cadastro realizado com sucesso",
          description: "Verifique seu email para confirmar a conta",
        });
      }

      return { error };
    } catch (error: any) {
      toast({
        title: "Erro no cadastro",
        description: "Ocorreu um erro inesperado",
        variant: "destructive",
      });
      return { error };
    }
  }, [toast]);

  const signOut = useCallback(async () => {
    // RASCUNHOS-NAVEGADOR (I.176): rascunhos e caches do navegador saem junto com a sessão
    clearScopedDrafts();
    try {
      const { error } = await supabase.auth.signOut();
      
      // ✅ Clear React Query cache to prevent data leakage between sessions
      try {
        queryClient.clear();
        logger.log('🧹 AUTH: QueryClient limpo no signOut');
      } catch (e) {
        logger.warn('⚠️ AUTH: Não foi possível limpar queryClient:', e);
      }
      
      if (!error) {
        toast({
          title: "Logout realizado",
          description: "Até logo!",
        });
      }
    } catch (error) {
      logger.error('Error signing out:', error);
    }
  }, [toast]);

  const resetPassword = useCallback(async (email: string) => {
    try {
      setAuthError(null);
      
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });
      
      if (error) {
        setAuthError(error.message);
        toast({
          title: "Erro ao enviar email",
          description: error.message,
          variant: "destructive",
        });
        return { error };
      }
      
      toast({
        title: "Email enviado!",
        description: "Verifique sua caixa de entrada para redefinir sua senha.",
      });
      
      return { error: null };
    } catch (error: any) {
      logger.error('Error in resetPassword:', error);
      setAuthError(error.message);
      toast({
        title: "Erro ao enviar email",
        description: "Ocorreu um erro inesperado",
        variant: "destructive",
      });
      return { error };
    }
  }, [toast]);

  const hasPermission = useCallback((requiredRole: UserRole): boolean => {
    if (userRoles.length === 0) return false;

    const requiredLevel = ROLE_HIERARCHY[requiredRole] ?? 999;

    // Multi-role: if ANY role meets the required level, grant access
    return userRoles.some(role => (ROLE_HIERARCHY[role] ?? 0) >= requiredLevel);
  }, [userRoles]);

  // ✅ PERF: O(1) Map indexado por "role:feature_key" em vez de busca linear O(n)
  const rolePermissionsMap = useMemo(() => {
    const map = new Map<string, boolean>();
    rolePermissionsCache.forEach(p => {
      map.set(`${p.role}:${p.feature_key}`, p.is_enabled);
    });
    return map;
  }, [rolePermissionsCache]);

  const canAccess = useCallback((feature: string, context?: string): boolean => {
    // §I.7 — deny by default durante loading
    if (isPermissionsLoading) return false;

    if (userRoles.length === 0) return false;

    // ✅ Master bypass — acesso total. ATO1-MENU (I.181): antes da checagem do mapa vazio — empresa sem linhas de
    // permissão do cargo master (cliente nova, EXTINSUL) monta o token do master sem chaves, e o master era negado
    // em tudo que dependia só de canAccess (menu Romaneios, criar romaneio)
    if (userRoles.includes('master')) return true;

    // §I.7 — nunca consultar legacyFallbackMap com mapa vazio
    if (rolePermissionsMap.size === 0) return false;

    // ✅ BUG-02/DRY-01 FIX: Para feature keys granulares (contendo ':'),
    // consultar o Map O(1) de role_permissions.
    if (feature.includes(':')) {
      if (rolePermissionsMap.size > 0) {
        return userRoles.some(role => {
          const key = `${role}:${feature}`;
          const value = rolePermissionsMap.get(key);
          return value === true;
        });
      }
      // ✅ UX FIX: Fallback permissivo enquanto cache não carregou
      const legacyFallbackMap: Record<string, string> = {
        'fin:': 'financial',
        'sales:': 'customers',
        'crm:': 'customers',
        'fiscal:': 'reports',
        'logistics:': 'loans',
        'stock:': 'deposits',
        'purchasing:': 'purchases',
        'admin:': 'settings',
      };
      const prefix = Object.keys(legacyFallbackMap).find(p => feature.startsWith(p));
      if (prefix) {
        const legacyKey = legacyFallbackMap[prefix];
        // ✅ GUARD: Prevenir recursão infinita — legacyKey nunca deve conter ':'
        if (legacyKey.includes(':')) return false;
        // Delegar ao mapa hardcoded legado abaixo
        return canAccess(legacyKey);
      }
      return false;
    }

    // Legacy keys (sem ':'): role_permissions tem precedência sobre mapa hardcoded
    if (rolePermissionsMap.size > 0) {
      const hasInMap = userRoles.some((role) => {
        const key = `${role}:${feature}`;
        return rolePermissionsMap.get(key) === true;
      });
      if (hasInMap) return true;
    }

    // Para chaves legadas simples (sem ':'), usar o mapa hardcoded como fallback
    const permissions: Record<UserRole, Record<string, boolean>> = {
      master: {
        dashboard: true, customers: true, employees: true, deposits: true,
        products: true, rentals: true, loans: true,
        reports: true, settings: true, create: true, edit: true, delete: true,
        financial: true, purchases: true,
      },
      gestor_comercial: {
        dashboard: true, customers: true, employees: false, deposits: false,
        products: true, rentals: false, loans: false,
        reports: true, settings: false, create: true, edit: true, delete: false,
        financial: false, purchases: false,
      },
      comprador: {
        dashboard: true, customers: false, employees: false, deposits: true,
        products: true, rentals: false, loans: false,
        reports: true, settings: false, create: true, edit: true, delete: false,
        financial: false, purchases: true,
      },
      estoquista: {
        dashboard: false, customers: false, employees: false, deposits: true,
        products: true, rentals: false, loans: false,
        reports: false, settings: false, create: true, edit: true, delete: false,
        financial: false, purchases: false,
      },
      vendedor_junior: {
        dashboard: false, customers: true, employees: false, deposits: false,
        products: true, rentals: false, loans: false,
        reports: false, settings: false, create: true, edit: false, delete: false,
        financial: false, purchases: false,
      },
      vendedor_senior: {
        dashboard: false, customers: true, employees: false, deposits: false,
        products: true, rentals: false, loans: false,
        reports: false, settings: false, create: true, edit: true, delete: false,
        financial: false, purchases: false,
      },
      financeiro_junior: {
        dashboard: false, customers: true, employees: false, deposits: false,
        products: false, rentals: false, loans: false,
        reports: true, settings: false, create: false, edit: false, delete: false,
        financial: true, purchases: false,
      },
      financeiro_senior: {
        dashboard: true, customers: true, employees: false, deposits: false,
        products: false, rentals: false, loans: false,
        reports: true, settings: false, create: true, edit: true, delete: false,
        financial: true, purchases: false,
      },
    };

    // Context-specific overrides per role
    const contextOverrides: Record<UserRole, Record<string, Record<string, boolean>>> = {
      master: {},
      gestor_comercial: {},
      comprador: {
        purchases: { create: true, edit: true, delete: false },
        products: { create: false, edit: false },
      },
      estoquista: {
        products: { create: true, edit: true },
      },
      vendedor_junior: {},
      vendedor_senior: {},
      financeiro_junior: {},
      financeiro_senior: {},
    };

    // Multi-role: if ANY role grants access, return true
    return userRoles.some(role => {
      if (context && contextOverrides[role]?.[context]?.[feature]) {
        return true;
      }
      return permissions[role]?.[feature] || false;
    });
  }, [userRoles, rolePermissionsMap, isPermissionsLoading]);

  // ✅ Expor refreshRolePermissions para que o RolePermissionsPanel possa
  // invalidar o cache em tempo real após mutações de toggle
  const refreshRolePermissions = useCallback(async () => {
    const currentCompanyId = company?.id;
    if (currentCompanyId) {
      await fetchRolePermissions(currentCompanyId);
      queryClient.invalidateQueries({ queryKey: ['feature-permission'] });
    }
  }, [company?.id, fetchRolePermissions, queryClient]);

  // Multiloja — Sprint 2: força re-leitura das claims de organização do JWT
  // Necessário após criar uma filial nova (TOKEN_REFRESHED não re-lê claims intencionalmente)
  const refreshOrgClaims = useCallback(async () => {
    try {
      const { data } = await supabase.auth.refreshSession();
      if (!data?.session) return;
      const claims = getJwtClaims(data.session);
      if (!claims) return;
      setOrganizationId(claims.organization_id ?? null);
      setAccessibleCompanyIds(claims.accessible_company_ids ?? []);
      setIsOrgMember(claims.is_org_member ?? false);
      setActiveCompanyId(prev => prev ?? claims.company_id);
      logger.log('✅ refreshOrgClaims: claims de organização atualizadas', {
        organizationId: claims.organization_id,
        accessibleCompanyIds: claims.accessible_company_ids,
      });
    } catch (e) {
      logger.error('❌ refreshOrgClaims: erro ao atualizar claims', e);
    }
  }, []);

  // Multiloja — Sprint 2: troca de contexto entre empresas do grupo
  const switchCompanyContext = useCallback(
    async (targetCompanyId: string | null) => {
      if (
        targetCompanyId !== null &&
        !accessibleCompanyIds.includes(targetCompanyId)
      ) {
        console.warn(
          '[AuthContext] Tentativa de switch para company não autorizada:',
          targetCompanyId
        );
        return;
      }
      await queryClient.invalidateQueries();
      setActiveCompanyId(targetCompanyId);
    },
    [accessibleCompanyIds, queryClient]
  );

  // ✅ PERF: Serialize unstable deps to primitives to prevent cascading re-renders
  const rolesKey = userRoles.join(',');
  const profileId = profile?.id;
  const profileComplete = profile?.isComplete;
  const companyId = company?.id;
  const companyActive = company?.active;
  const companySub = company?.subscription_expires_at;
  const tenantStatus = company?.tenant_status;

  const value = useMemo(() => ({
    user,
    session,
    userRole,
    userRoles,
    profile,
    company,
    loading,
    isPermissionsLoading,
    needsCompany,
    subscriptionExpired,
    signIn,
    signUp,
    signOut,
    resetPassword,
    hasPermission,
    canAccess,
    updateUserCompany,
    createCompany,
    fetchUserProfile,
    refreshProfile,
    refreshRolePermissions,
    authError,
    debugInfo,
    organizationId,
    accessibleCompanyIds,
    isOrgMember,
    activeCompanyId,
    switchCompanyContext,
    refreshOrgClaims,
  }), [
    user?.id, session?.access_token, userRole, rolesKey,
    profileId, profileComplete,
    companyId, companyActive, companySub, tenantStatus,
    loading, isPermissionsLoading, needsCompany, subscriptionExpired,
    signIn, signUp, signOut, resetPassword,
    hasPermission, canAccess,
    updateUserCompany, createCompany, fetchUserProfile,
    refreshProfile, refreshRolePermissions,
    authError, debugInfo,
    organizationId, accessibleCompanyIds, isOrgMember, activeCompanyId,
    switchCompanyContext, refreshOrgClaims,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};